// Pay & Hours — People tab. Driven from exactly three sources, computed
// server-side (src/pay_hours.js): register lines, People + rate history, and
// owner rulings (owner, 2026-09-08: "driven from very few points of
// information ... nothing copied ... make it amazing"). This component adds
// no numbers of its own — every value here is what the server already sent.
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { session } from '../lib/session';
import { useResource } from '../lib/store';
import { visibleClinics, activeClinicFrom } from './ClinicTabs';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { payPeriodLabel } from '../lib/payPeriodLabel';
import { formatDate } from '../lib/date';
import { decisionCaption } from '../lib/hourRaises';
import { EmptyState, FaultState } from './FaultState';
import {
  fetchPayHours, payHoursKey, type PayHoursRow, type RaiseStatus,
} from '../lib/payHours';
import { ROLE_BUCKETS, ROLE_BUCKET_TITLE, roleBucket, type RoleBucket } from '../lib/roleBuckets';

const SERVICE_ROLES = new Set(['LMT', 'EST']);

function num(v: number | null | undefined, digits = 1): string {
  return v == null || Number.isNaN(v) ? '—' : v.toFixed(digits);
}

/** Local, like PeopleManagement.tsx's own `money2` — this codebase keeps a
 * per-component formatter rather than one shared currency helper. */
function money2(v: number | null | undefined): string {
  return v == null || Number.isNaN(v) ? '—' : `$${v.toFixed(2)}`;
}

function dash(v: string | null | undefined): string {
  return v ? formatDate(v) : '—';
}

interface Pill { label: string; cls: string }

const RAISE_PILL: Record<RaiseStatus, Pill> = {
  crossed: { label: 'crossed this period', cls: 'badge warn' },
  due: { label: 'raise due next payroll', cls: 'badge warn' },
  moved: { label: 'moved', cls: 'badge' },
  rejected: { label: 'rejected', cls: 'badge bad' },
};

/** Every pill a row earns, in order — a provider can carry more than one at
 * once (Cassandra Barrett: rejected AND terminating next cycle). Nothing
 * here is prioritized away; the spec's row states are independent facts,
 * not one mutually-exclusive status. */
function pillsFor(row: PayHoursRow): Pill[] {
  const pills: Pill[] = [];
  if (row.raise) pills.push(RAISE_PILL[row.raise.status]);
  if (row.anniversary?.bonusDue && !row.anniversary.paidWeek) {
    pills.push(row.anniversary.bonusUnproven
      ? { label: 'bonus — check', cls: 'badge warn' }
      : { label: 'bonus due', cls: 'badge warn' });
  }
  if (row.termination?.code === 'PAID_NEXT_CYCLE') {
    pills.push({ label: 'terminating next cycle', cls: 'badge bad' });
  }
  return pills;
}

function RowPills({ row }: { row: PayHoursRow }): JSX.Element | null {
  const pills = pillsFor(row);
  if (!pills.length) return null;
  return (
    <div class="pay-hours-pills" data-testid={`pay-hours-pills-${row.employeeId}`}>
      {pills.map((p) => <span key={p.label} class={p.cls}>{p.label}</span>)}
    </div>
  );
}

function NextRaiseCell({ row }: { row: PayHoursRow }): JSX.Element {
  if (!row.raise) return <>—</>;
  const { raise } = row;
  // No clinic argument: `raise.nextRaise` already carries THIS clinic's own
  // dates (src/pay_hours.js scopes to one clinic per call, unlike the
  // multi-clinic `following` src/hour_raise.js hands RaiseAlerts.tsx). Reuse
  // decisionCaption exactly as RaiseAlerts.tsx does — same shape, same
  // "Rejected by X on Y — reason — due again" / "Moved to Week NN by X on Y"
  // wording, so a decision never reads two different ways in two places.
  const label = raise.nextRaise ? payPeriodLabel(raise.nextRaise) : '—';
  const caption = decisionCaption(raise.decision, raise.nextRaise);
  return (
    <>
      <div class={raise.status === 'rejected' ? 'bad' : undefined}>{label}</div>
      {caption ? <div class="muted" data-testid={`pay-hours-decision-${row.employeeId}`}>{caption}</div> : null}
    </>
  );
}

function AnniversaryCell({ row }: { row: PayHoursRow }): JSX.Element {
  if (!row.anniversary) return <>—</>;
  const a = row.anniversary;
  return (
    <>
      <div>{num(a.hoursInWindow)} / 1,200</div>
      <div class="muted">{formatDate(a.windowStart)} – {formatDate(a.windowEnd)}</div>
      {a.reached ? (
        <div class="ok" data-testid={`anniv-earned-${row.employeeId}`}>
          Earned {money2(a.earnedAmount ?? null)} — paid at anniversary {formatDate(a.windowEnd)}
        </div>
      ) : null}
      {a.bonusDue ? (
        <div class={a.paidWeek ? 'ok' : 'bad'} data-testid={`anniv-due-${row.employeeId}`}>
          {a.paidWeek
            ? `Anniversary ${formatDate(a.windowStart)} bonus ${money2(a.bonusAmount)} — paid ${payPeriodLabel(a.paidWeek)}`
            : `Anniversary ${formatDate(a.windowStart)}: bonus ${money2(a.bonusAmount)} due now (${num(a.lastYearHours ?? 0)} hrs)`}
        </div>
      ) : null}
      {a.bonusDue && !a.paidWeek && a.bonusUnproven ? (
        <div class="bad" data-testid={`anniv-unproven-${row.employeeId}`}>
          Check: {num(a.lastYearHours ?? 0)} hrs before the anniversary payroll. It reaches 1,200 only if enough of
          that payroll's {num(a.anniversaryPayrollHours ?? 0)} hrs were worked before {formatDate(a.windowStart)}.
        </div>
      ) : null}
      {a.bonusDue && !a.paidWeek && a.otherBonuses?.length ? (
        <div class="muted" data-testid={`anniv-other-${row.employeeId}`}>
          {a.otherBonuses.map((b) => `${money2(b.amount)} bonus ${payPeriodLabel(b)}`).join('; ')} — not the 1,200-hour amount.
        </div>
      ) : null}
    </>
  );
}

function PayHoursTable({ rows, grouped = false }: { rows: PayHoursRow[]; grouped?: boolean }): JSX.Element {
  return (
    <div class="table-wrap pay-hours-wrap">
      <table class={grouped ? 'table pay-hours-table grouped' : 'table pay-hours-table'} data-testid="pay-hours-table">
        {/* One table per card, so fixed widths are what make the columns of
            the Manager, Front Desk and LMT/EST cards line up (the same
            reason People's roster cards carry a colgroup). */}
        {grouped ? (
          <colgroup>
            <col style="width: 19%" />
            <col style="width: 8%" />
            <col style="width: 9%" />
            <col style="width: 8%" />
            <col style="width: 8%" />
            <col style="width: 12%" />
            <col style="width: 12%" />
            <col style="width: 14%" />
            <col style="width: 10%" />
          </colgroup>
        ) : null}
        <thead>
          <tr>
            <th class="name">Name</th>
            <th>Rate now</th>
            <th>Effective since</th>
            <th>Raised from</th>
            <th>Hours since raise</th>
            <th>Toward next 1,000</th>
            <th>Next raise</th>
            <th>Anniversary year</th>
            <th>Last paid</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.employeeId}
              data-testid={`pay-hours-row-${r.employeeId}`}
              class="pay-hours-row"
              // Same in-page navigation People.tsx's own row-open uses (a
              // hash change on the SAME path never reloads the page) — not
              // `window.location.href`, which would.
              onClick={() => { window.location.hash = `person-${r.employeeId}`; }}
            >
              <td class="name">
                <a
                  class="link"
                  href={`/people#person-${r.employeeId}`}
                  data-testid={`pay-hours-open-${r.employeeId}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <strong>{r.fullName}</strong>
                </a>
                <div class="muted">{r.role}{r.fileNo ? ` · #${r.fileNo}` : ''}</div>
                <RowPills row={r} />
              </td>
              <td class="num">{money2(r.rateNow)}</td>
              <td>{dash(r.effectiveSince)}</td>
              <td class="num">{money2(r.raisedFrom)}</td>
              <td class="num">{SERVICE_ROLES.has(r.role) ? num(r.hoursSinceRaise) : '—'}</td>
              <td class="num">
                {r.raise ? (
                  <>
                    {num(r.raise.towardNext)} / {num(r.raise.hoursToGo)} to go
                    {r.raise.milestones ? (
                      <div class="muted">{r.raise.milestones} milestone{r.raise.milestones === 1 ? '' : 's'}</div>
                    ) : null}
                  </>
                ) : '—'}
              </td>
              <td><NextRaiseCell row={r} /></td>
              <td><AnniversaryCell row={r} /></td>
              <td>{r.lastPaid ? payPeriodLabel(r.lastPaid) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Rows split into People's buckets, in People's order. The server's own row
 * order is kept inside each bucket. */
export function groupPayHoursRows(rows: PayHoursRow[]): Record<RoleBucket, PayHoursRow[]> {
  const out: Record<RoleBucket, PayHoursRow[]> = { MANAGER: [], FRONT_DESK: [], SERVICE: [], OTHER: [] };
  for (const r of rows) out[roleBucket(r.role)].push(r);
  return out;
}

/** The People tab's Pay & Hours: one card per bucket, like People itself
 * (owner, 2026-09-23: "manager, front desk associates and then LMTs and ESTs,
 * just like under people"). An empty named bucket says so, as People does;
 * "Other roles" appears only when someone is in it. */
function PayHoursGrouped({ rows }: { rows: PayHoursRow[] }): JSX.Element {
  const groups = groupPayHoursRows(rows);
  return (
    <>
      {ROLE_BUCKETS.filter((b) => b !== 'OTHER' || groups.OTHER.length > 0).map((bucket) => (
        <section class="card" key={bucket} data-testid={`pay-hours-group-${bucket}`}>
          <div class="section-head">
            <h2>{ROLE_BUCKET_TITLE[bucket]}</h2>
          </div>
          {groups[bucket].length === 0
            ? <p class="empty">Nobody in this group at this clinic.</p>
            : <PayHoursTable rows={groups[bucket]} grouped />}
        </section>
      ))}
    </>
  );
}

/**
 * A clinic switcher scoped to just the four clinics — deliberately NOT the
 * shared `<ClinicTabs>` component. `PayHours` mounts INSIDE People's own
 * clinic-tabbed roster view (as one of its "after" tabs), which already has
 * its own Management entry; `<ClinicTabs>` auto-appends a SECOND "Management"
 * tab for every owner/director session with no way to opt out, which would
 * put two Management buttons on one screen. Same markup and CSS classes
 * `<ClinicTabs>` uses (`.clinic-row` / `.clinic-tabs` / `.tab` / `.tab.active`
 * — all already covered by designlaw), just without that auto-append.
 */
export function PayHoursClinicSwitcher(
  { clinics, active, onSelect }: { clinics: LocationCode[]; active: LocationCode; onSelect: (code: LocationCode) => void },
): JSX.Element | null {
  if (clinics.length < 2) return null;
  return (
    <div class="clinic-row">
      <div class="clinic-tabs" role="tablist" aria-label="Clinic">
        {clinics.map((code) => (
          <button
            key={code}
            type="button"
            role="tab"
            aria-selected={code === active}
            class={code === active ? 'tab active' : 'tab'}
            onClick={() => onSelect(code)}
          >
            {LOCATION_NAMES[code] ?? code}
          </button>
        ))}
      </div>
    </div>
  );
}

/** One clinic's Pay & Hours table with its own load, no switcher and no
 * section head — the My Desk panel (owner, 2026-09-10: the desk item
 * "should be the Pay and Hours Table"). Same key, same fetch, same table as
 * the People tab below, so the two can never disagree. */
export function PayHoursBoard({ clinic }: { clinic: LocationCode }): JSX.Element {
  const load = useResource(payHoursKey(clinic), () => fetchPayHours(clinic));
  if (load.status === 'loading') return <EmptyState label="Loading Pay & Hours…" />;
  if (load.status === 'fault') return <FaultState name="pay_hours_load_failed" detail={load.error} />;
  if (load.data.rows.length === 0) {
    return <EmptyState label={`No active people on file for ${LOCATION_NAMES[clinic] ?? clinic}.`} />;
  }
  return <PayHoursTable rows={load.data.rows} />;
}

/** `initialClinic` lets a Desk-card link land straight on a clinic (owner,
 * "linking to the tab") — People.tsx reads `#pay-hours` / `#pay-hours-<CO>`
 * from the hash the same way it already reads `#person-<id>`. */
export function PayHours({ initialClinic }: { initialClinic?: LocationCode | null }): JSX.Element {
  const me = session.value.me;
  const clinics = visibleClinics();
  const [clinic, setClinic] = useState<LocationCode>(
    () => activeClinicFrom(initialClinic ?? null, clinics)
  );

  // A later Desk-card navigation (same mounted page, hash changes) should
  // still move the switcher — this mirrors the existing #person- hashchange
  // pattern on People.tsx rather than inventing a second one.
  useEffect(() => {
    if (initialClinic && clinics.includes(initialClinic)) setClinic(initialClinic);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialClinic]);

  const key = payHoursKey(clinic);
  const load = useResource(key, () => fetchPayHours(clinic));

  return (
    <div data-testid="pay-hours-tab">
      <PayHoursClinicSwitcher clinics={clinics} active={clinic} onSelect={setClinic} />
      <div class="section-head">
        <h2>Pay &amp; Hours</h2>
        <p>
          {LOCATION_NAMES[clinic] ?? clinic} — rate, hours since the last raise, and the annual bonus window,
          computed from register lines, People, and owner rulings. Nothing here is typed in twice.
        </p>
      </div>

      {load.status === 'loading' ? <EmptyState label="Loading Pay & Hours…" /> : null}
      {load.status === 'fault' ? <FaultState name="pay_hours_load_failed" detail={load.error} /> : null}
      {load.status === 'ready' ? (
        load.data.rows.length === 0 ? (
          <EmptyState label={`No active people on file for ${LOCATION_NAMES[clinic] ?? clinic}.`} />
        ) : (
          <PayHoursGrouped rows={load.data.rows} />
        )
      ) : null}
      {!me ? <FaultState name="pay_hours_no_session" detail="Sign in to see Pay & Hours." /> : null}
    </div>
  );
}
